import { cn } from "@/lib/utils";
import type { ParsedAssistant, Source } from "@/lib/types";
import { Markdown } from "@/components/Markdown";
import { ArrowRight, BookOpen, Link2 } from "lucide-react";
import { useState } from "react";

function faviconUrl(url: string) {
  try {
    const u = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=64`;
  } catch {
    return "";
  }
}

function hostname(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function SourceCard({
  source,
  index,
  size = "sm",
}: {
  source: Source;
  index: number;
  size?: "sm" | "lg";
}) {
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      className={cn(
        "group flex flex-col gap-2 rounded-lg border border-border/60 bg-card/50 transition-all hover:border-primary/40 hover:bg-card",
        size === "sm" ? "p-3 text-xs" : "p-4 text-sm",
      )}
    >
      <div
        className={cn(
          "leading-snug text-foreground/90",
          size === "sm" ? "line-clamp-2" : "line-clamp-3",
        )}
      >
        {source.title || hostname(source.url)}
      </div>
      <div className="mt-auto flex items-center gap-1.5">
        {faviconUrl(source.url) ? (
          <img
            src={faviconUrl(source.url)}
            alt=""
            className="size-3.5 rounded-sm"
          />
        ) : null}
        <span className="truncate text-muted-foreground">
          {hostname(source.url)}
        </span>
        <span className="ml-auto font-mono text-muted-foreground/60">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
    </a>
  );
}

function SourcesPreview({
  sources,
  onMoreClick,
}: {
  sources: Source[];
  onMoreClick: () => void;
}) {
  if (!sources.length) return null;
  const visible = sources.slice(0, 4);
  const extra = sources.length - visible.length;
  return (
    <div className="mb-5">
      <div className="mb-2.5 flex items-center gap-2">
        <Link2 className="size-3.5 text-primary" strokeWidth={2.4} />
        <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Sources
        </span>
        <span className="text-xs text-muted-foreground/60">
          · {sources.length}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {visible.map((s, i) => (
          <SourceCard key={i} source={s} index={i} />
        ))}
        {extra > 0 ? (
          <button
            onClick={onMoreClick}
            className="flex items-center justify-center rounded-lg border border-dashed border-border/60 p-3 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
          >
            +{extra} more
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SourcesGrid({ sources }: { sources: Source[] }) {
  if (!sources.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No sources for this answer.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {sources.map((s, i) => (
        <SourceCard key={i} source={s} index={i} size="lg" />
      ))}
    </div>
  );
}

function AnswerBody({
  answer,
  streaming,
}: {
  answer: string;
  streaming?: boolean;
}) {
  return (
    <div className="mb-6">
      <div className="mb-2.5 flex items-center gap-2">
        <BookOpen className="size-3.5 text-primary" strokeWidth={2.4} />
        <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Answer
        </span>
      </div>
      {answer.length === 0 && streaming ? (
        <div className="space-y-2">
          <div className="shimmer h-3 w-[90%] rounded" />
          <div className="shimmer h-3 w-[78%] rounded" />
          <div className="shimmer h-3 w-[60%] rounded" />
        </div>
      ) : (
        <div className="break-words">
          <Markdown text={answer} />
          {streaming ? (
            <span className="ml-1 inline-block size-2.5 translate-y-[-1px] animate-pulse rounded-full bg-primary" />
          ) : null}
        </div>
      )}
    </div>
  );
}

function FollowUps({
  followUps,
  onPick,
}: {
  followUps: string[];
  onPick: (q: string) => void;
}) {
  if (!followUps.length) return null;
  return (
    <div className="mt-2">
      <div className="mb-2.5 flex items-center gap-2">
        <ArrowRight className="size-3.5 text-primary" strokeWidth={2.4} />
        <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Related
        </span>
      </div>
      <div className="divide-y divide-border/60 border-y border-border/60">
        {followUps.map((q, i) => (
          <button
            key={i}
            onClick={() => onPick(q)}
            className="group flex w-full items-center justify-between gap-4 py-3 text-left text-sm text-foreground/90 transition-colors hover:text-primary sm:text-[15px]"
          >
            <span>{q}</span>
            <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-primary" />
          </button>
        ))}
      </div>
    </div>
  );
}

type Tab = "answer" | "sources";

export function AssistantTurn({
  query,
  parsed,
  streaming,
  onPickFollowUp,
}: {
  query: string;
  parsed: ParsedAssistant;
  streaming: boolean;
  onPickFollowUp: (q: string) => void;
}) {
  const [tab, setTab] = useState<Tab>("answer");

  return (
    <article className="fade-up border-b border-border/40 pb-8 sm:pb-10">
      <h2 className="font-serif text-2xl leading-tight tracking-tight text-foreground sm:text-3xl md:text-4xl">
        {query}
      </h2>

      <div className="mt-4 flex items-center gap-1 border-b border-border/50 sm:mt-5">
        <button
          onClick={() => setTab("answer")}
          className={cn(
            "relative px-1 pb-2 text-sm transition-colors",
            tab === "answer"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <span className="flex items-center gap-1.5">
            <BookOpen className="size-3.5" />
            Answer
          </span>
          {tab === "answer" ? (
            <span className="absolute -bottom-px left-0 right-0 h-px bg-primary" />
          ) : null}
        </button>
        <button
          onClick={() => setTab("sources")}
          className={cn(
            "relative ml-3 px-1 pb-2 text-sm transition-colors",
            tab === "sources"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <span className="flex items-center gap-1.5">
            <Link2 className="size-3.5" />
            Sources
            <span
              className={cn(
                "rounded-full px-1.5 py-px font-mono text-[10px]",
                tab === "sources"
                  ? "bg-primary/20 text-primary"
                  : "bg-secondary text-muted-foreground",
              )}
            >
              {parsed.sources.length}
            </span>
          </span>
          {tab === "sources" ? (
            <span className="absolute -bottom-px left-0 right-0 h-px bg-primary" />
          ) : null}
        </button>
      </div>

      <div className="mt-5 sm:mt-6">
        {tab === "answer" ? (
          <>
            <SourcesPreview
              sources={parsed.sources}
              onMoreClick={() => setTab("sources")}
            />
            <AnswerBody answer={parsed.answer} streaming={streaming} />
            <FollowUps
              followUps={parsed.followUps}
              onPick={(q) => !streaming && onPickFollowUp(q)}
            />
          </>
        ) : (
          <SourcesGrid sources={parsed.sources} />
        )}
      </div>
    </article>
  );
}
